import {
  ShieldCheck,
  ClipboardCheck,
  Wrench,
  FileText,
  ActivitySquare,
  HardHat,
  Network,
  Radar,
  Satellite,
  Target,
  Ruler,
  Cpu,
  MapPin,
  Award,
  Megaphone,
  Truck,
  Sparkles,
  Paintbrush,
  Building2,
  AlertTriangle,
  Users,
  Settings,
} from "lucide-react";

export const LOGO_URL = "/media/forgedops_lockup-640.webp";
export const HERO_LOGO_URL = "/media/forgedops_full-768.webp";
// Big horizontal forged-metal lockup w/ tagline embedded — for hero sections / closing brand authority blocks
export const LOCKUP_V2_URL = "/media/forgedops_lockup-640.webp";

export const MEDIA = {
  // Hero background video for Home page — 720x1280 portrait, H.264 MP4 with rotation baked in,
  // faststart enabled, ~9.6 MB. Cross-browser playable (Safari, Chrome, Firefox, Edge, iOS/Android).
  homeHeroVideo: "/media/forgedops_hero.mp4",
  homeHeroPoster: "/media/forgedops_hero_poster.jpg",
  // Trimmed & re-encoded jobsite clip (3.7 MB MP4, 1280x720, 20s, served from /media/)
  fieldClipMp4: "/media/judd_field_clip.mp4",
  fieldClipPoster: "/media/judd_field_poster.jpg",
  // Real client/jobsite imagery uploaded by user
  graderGps:
    "https://customer-assets.emergentagent.com/job_judd-team/artifacts/1jcuz7dk_IMG_1143%20%281%29.JPG",
  bridgeBw:
    "https://customer-assets.emergentagent.com/job_judd-team/artifacts/twqiw71i_IMG_0471.JPG",
  pavingTrain:
    "https://customer-assets.emergentagent.com/job_judd-team/artifacts/qognyu5e_IMG_0972.jpg",
  slipformHighway:
    "https://customer-assets.emergentagent.com/job_judd-team/artifacts/ek2cuntv_IMG_0971.JPG",
  // Old tech photos (kept for fallback / reuse in marquees)
  techTrainingLegacy:
    "https://customer-assets.emergentagent.com/job_judd-team/artifacts/875jteny_TECH.jpg",
  techCalibrationLegacy:
    "https://customer-assets.emergentagent.com/job_judd-team/artifacts/6h4r9gyl_TECH2.jpg",
  // Tech page hero panels — new field-shot pairing
  // Landscape: total station foreground + JD grader background (perfect for "Calibrated on the ground")
  techCalibration: "/media/new_field3.jpg",
  // Portrait: two surveyors operating a Topcon total station ("Trained until it sticks")
  techTraining: "/media/new_field2.jpg",
  soilStabilizer:
    "https://customer-assets.emergentagent.com/job_judd-team/artifacts/az8jrvmh_MIXER.JPG",
  // Newer field photography
  techOnsite:
    "https://customer-assets.emergentagent.com/job_judd-team/artifacts/7mh5x8ki_Tech%20Onsite.jpeg",
  topconController:
    "https://customer-assets.emergentagent.com/job_judd-team/artifacts/ocrii4vl_topcon_fc-6400-photo2_240711app-web.jpeg",
  // Local field photo set
  fieldA: "/media/field_a.jpg", // JD grader airfield (existing)
  fieldB: "/media/field_b.jpg",
  fieldC: "/media/field_c.jpg",
  fieldD: "/media/field_d.jpg", // JD grader + steel post on graded subbase
  // New field uploads (HEIC → JPG)
  fieldGraderAirfield: "/media/new_field1.jpg", // JD grader, control tower in distance
  fieldSurveyors: "/media/new_field2.jpg",      // Two surveyors w/ Topcon total station
  fieldStationGrader: "/media/new_field3.jpg",   // Total station foreground + grader bg
  fieldGraderCurb: "/media/new_ohlong.jpg",      // JD grader along fresh concrete curb
  // Project / operations imagery (Feb 2026 batch)
  opTwilightTech: "/media/op_a.jpg",         // Twilight Trimble total station + Wirtgen reclaimer + crew on airfield
  opPaving: "/media/op_b.jpg",                // Highway concrete paving train + dump trucks
  opNightCranes: "/media/op_c.jpg",          // Tandem cranes lifting precast bridge beam at night
  opTwilightCranes: "/media/op_d.jpg",       // Twilight bridge beam set with lens flare
  opsIntelAerial: "/media/ops_intel_aerial.jpg", // Aerial drone shot w/ AR equipment-tracking overlays
  opFlagPaver: "/media/op_flag_paver.jpg",   // Slipform paver w/ American flag flying — patriotic, gritty
  // Real-crew operational photography (Feb 2026 batch)
  opConcreteCrew: "/media/op_concrete_crew.jpg",   // 5+ crew + foreman around concrete pump pour into yellow formwork
  opCrewMeeting: "/media/op_crew_meeting.jpg",     // 3-man crew assembling blue water main + red fitting
  opUtilityTrench: "/media/op_utility_trench.jpg", // Clean blue PVC water main in trench, vivid blue sky
  opNightUtility: "/media/op_night_utility.jpg",   // Night utility tie-in w/ flood lights + lens flare
  opAirfieldDrain: "/media/op_airfield_drain.jpg", // Airfield drainage box install + compactor crew (FAA)
  opSoilTest: "/media/op_soil_test.jpg",           // 2-man crew running soil density / compaction test
  opPipeLift: "/media/op_pipe_lift.jpg",           // Massive precast concrete drainage pipe lifted by excavator
  opDrillPour: "/media/op_drill_pour.jpg",         // Concrete truck pouring around auger drill rig
  opSlipform: "/media/op_slipform.jpg",            // Slipform sidewalk paving + crew + concrete truck (residential)
  // Iter 6.3 — Real field photography pulled from owner Google Photos albums. Each one passed strict OSHA safety review before site use.
  forgeAirfieldWalk: "/media/forge_airfield_walk.jpg",            // Crew in PPE walking taxiway under overcast — quiet operational scale
  forgeAirfieldTruck: "/media/forge_airfield_truck.jpg",          // Pickup + barrel trailer + crew on airfield taxiway (FAA op real)
  forgeAirfieldExcavator: "/media/forge_airfield_excavator.jpg",  // Yellow excavator + crew in distance on wet airfield surface
  forgeEarthwork: "/media/forge_earthwork.jpg",                   // Heavy equipment + earthmoving + industrial backdrop (Magellan Dallas)
  forgeConcretePour: "/media/forge_concrete_pour.jpg",            // Slurry discharge into excavated form — production close-up
  forgeUtilityInstall: "/media/forge_utility_install.jpg",        // Drainage grates + concrete + gravel — underground utility infra
  forgeBarrierLane: "/media/forge_barrier_lane.jpg",              // Long red/white temporary barrier corridor — iconic infra cinematic
  forgePipeYard: "/media/forge_pipe_yard.jpg",                    // Site sign + large white pipes + barriers + heavy equipment
  forgeExcavationActive: "/media/forge_excavation_active.jpg",    // Excavator digging into earth pile + storm pipes — active operations
};

export const COMPANY = {
  name: "FORGEDOPS",
  legalName: "ForgedOps",
  tagline: "Building Safer Jobs | Powering Performance",
  established: "2015",
  location: "Florida, USA",
  origin: "Forged in Texas · Est 2015",
  coverage: "Serving heavy civil contractors nationwide",
  email: "info@forgedopshq.com",
  phone: "904-297-8419",
};

/* Five core pillars */
export const PILLARS = [
  {
    id: "operations-systems",
    kicker: "Pillar 01",
    icon: Network,
    title: "Stop Losing Control of Field Operations",
    subtitle: "Operations Systems",
    short: "Custom digital platforms built around how your crews actually work.",
    description:
      "Custom construction operations systems — safety, inspections, maintenance, reporting, and project management — woven into one platform that survives the jobsite.",
    bullets: [
      "Reduce operational bottlenecks across crews & shifts",
      "Improve field accountability — equipment, inspections, daily reports",
      "Strengthen safety execution (OSHA / DOT / FAA aligned)",
      "Eliminate paperwork gaps that kill production",
      "Standardize field operations across projects & regions",
    ],
    image: "/media/op_crew_meeting.jpg",
    pageLink: "/core",
  },
  {
    id: "consulting",
    kicker: "Pillar 02",
    icon: Radar,
    title: "Field Leadership That Stays Through Execution",
    subtitle: "Operations & Field Consulting",
    short: "Field-forged advisory for heavy civil and highway operations.",
    description:
      "Operations leaders who've actually run the work. We don't hand you a binder and walk away — we stay engaged, on call, accountable through execution.",
    bullets: [
      "Increase production visibility across active projects",
      "Reduce risk before incidents happen — proactive audits",
      "Strengthen project execution strategy & scheduling",
      "Develop field workflows your foremen will actually follow",
      "Stand up new operations or recover failing ones",
    ],
    image: "/media/op_soil_test.jpg",
    engagement: ["Phone", "Virtual meetings", "Onsite consulting"],
  },
  {
    id: "technology",
    kicker: "Pillar 03",
    icon: Satellite,
    title: "Machine Control That Improves Production",
    subtitle: "Construction Technology Services",
    short: "Machine-control support, model building, quantity takeoffs, and cut/fill analysis.",
    description:
      "Machine-control troubleshooting, GPS, robotic total stations, and LPS setup and training, plus model building, quantity takeoffs, and cut/fill analysis. Scope, pricing, and scheduling are agreed before work.",
    bullets: [
      "GPS machine control training & rollout",
      "Robotic Total Station training",
      "LPS (Local Positioning System) training",
      "Machine control setup & optimization",
      "Site calibrations & calibration training",
      "Construction model building, quantity takeoffs, and cut/fill analysis",
    ],
    image: "/media/op_a.jpg",
    pageLink: "/technology",
  },
  {
    id: "safety-training",
    kicker: "Pillar 04",
    icon: ShieldCheck,
    title: "Field-Led Safety & OSHA Outreach Training",
    subtitle: "Safety & OSHA Outreach Training",
    short:
      "OSHA 10 & 30-Hour Outreach, onsite stand-downs, and field-led safety leadership development.",
    description:
      "OSHA 10 & 30-Hour Outreach delivered by instructors who've actually run heavy civil work. Onsite, in-yard, or in your training room — built so crews respect it.",
    bullets: [
      "OSHA 10-Hour Outreach Training (onsite or in-yard)",
      "OSHA 30-Hour Outreach Training for supers & foremen",
      "Heavy civil-focused safety instruction (not generic)",
      "Jobsite safety stand-downs & tailgate meetings",
      "Field leadership & accountability training",
      "OSHA-compliant program development & audits",
    ],
    image: "/media/forge_safety_standup.jpg",
    pageLink: "/training",
  },
  {
    id: "branding",
    kicker: "Pillar 05",
    icon: Award,
    title: "Operational Identity & Professional Field Presence",
    subtitle: "Operational Identity Systems",
    short: "Field-first operational identity for heavy civil contractors.",
    description:
      "Branded operational systems, white-label platforms, bilingual workflows, fleet identity, and crew culture that ties it all together — so your operation looks as professional as it runs.",
    bullets: [
      "White-labeled operational platforms",
      "Bilingual (EN/ES) field workflows",
      "Branded reports, forms & dashboards",
      "Branded onboarding & training kits",
      "Fleet, equipment & site signage identity",
      "Branded customer portals & QR systems",
    ],
    image: "/media/operational_identity-1280.webp",
    flatImage: true,
    pageLink: "/branding",
  },
];

/* System modules for the dedicated Systems page */
export const SYSTEM_MODULES = [
  {
    icon: ShieldCheck,
    title: "Safety & Compliance",
    description:
      "Incident reporting, toolbox talks, JSAs, near-miss tracking, and compliance documentation — built around OSHA, DOT, and FAA requirements.",
  },
  {
    icon: ClipboardCheck,
    title: "Equipment Inspections",
    description:
      "Daily walkarounds, deficiency logs, photo-backed inspections, and automatic flags for out-of-service equipment. Operators own it; supervisors see it.",
  },
  {
    icon: Wrench,
    title: "Maintenance & Shop Communication",
    description:
      "Work orders from field to shop, parts requests, PM scheduling, and repair history on every unit. Ends the radio-and-text coordination.",
  },
  {
    icon: FileText,
    title: "Daily Reporting Automation",
    description:
      "Crew logs, production quantities, materials, weather, and delay tracking — captured once, rolled up to ops and accounting automatically.",
  },
  {
    icon: Settings,
    title: "Project Management Integration",
    description:
      "Plug into the schedule and budget you already run. Field data feeds back into cost codes, quantities, and forecasts without double entry.",
  },
  {
    icon: ActivitySquare,
    title: "Operational Dashboards",
    description:
      "Owners, ops, and PMs see what matters — equipment health, safety performance, production rates — on one pane of glass.",
  },
];

/* Construction technology capabilities for /technology */
export const TECH_CAPABILITIES = [
  {
    icon: Satellite,
    title: "GPS Machine Control",
    description:
      "Setup, rollout, and training for GPS machine control on dozers, graders, and rovers. We deploy it right the first time and train the operators to own it.",
  },
  {
    icon: Target,
    title: "Robotic Total Stations",
    description:
      "Hands-on training and deployment for robotic total stations. Fewer stakes, less rework, faster cycles — without sacrificing accuracy.",
  },
  {
    icon: Cpu,
    title: "LPS (Local Positioning Systems)",
    description:
      "Local positioning system setup and training for jobs where GPS can't reach — tunnels, canyons, heavily obstructed sites.",
  },
  {
    icon: Ruler,
    title: "Site Calibration",
    description:
      "Proper calibration separates good data from garbage. We run calibrations and train your team to run them too, so your models work in the field.",
  },
  {
    icon: Network,
    title: "Machine Control Integration",
    description:
      "End-to-end integration across your fleet — dozers, graders, slipform pavers, and rovers — all talking to the same accurate model.",
  },
  {
    icon: HardHat,
    title: "Field Operator Coaching",
    description:
      "Technology only performs if your crews trust it. We coach operators, foremen, and supers until the tech is faster than the old way.",
  },
];

export const TECH_EQUIPMENT = [
  "Dozers",
  "Motor Graders",
  "Rovers",
  "Slipform Pavers",
  "Excavators",
  "Total Stations",
];

export const SEALED_MODEL_BENEFITS = [
  {
    icon: Award,
    title: "Higher Accuracy",
    body: "Coordinate the source drawings, surfaces, and required tolerances with the model builder and field team.",
  },
  {
    icon: ShieldCheck,
    title: "Defined Review Responsibilities",
    body: "Professional review and sealing requirements are defined for the project and arranged within the agreed scope.",
  },
  {
    icon: Satellite,
    title: "Better Machine Control Performance",
    body: "Prepare model deliverables for the intended equipment and verify their use with the field team.",
  },
  {
    icon: AlertTriangle,
    title: "Reduced Field Errors",
    body: "Review model inputs, coordinate checks, and field verification to help identify discrepancies before production.",
  },
];

/* Branding & Growth offerings for /branding */
export const BRANDING_OFFERINGS = [
  {
    icon: Building2,
    title: "Business Development Strategy",
    description:
      "Market positioning, pursuit strategy, and client targeting built specifically for heavy civil and highway contractors.",
  },
  {
    icon: Sparkles,
    title: "Brand Identity & Refinement",
    description:
      "Not just a logo — a complete visual system that communicates capability, safety culture, and professionalism from the first impression.",
  },
  {
    icon: Paintbrush,
    title: "Logo Design & Systems",
    description:
      "Custom logos designed to survive being printed on a hard hat, a truck door, and a bid cover letter. Durable across every surface.",
  },
  {
    icon: Megaphone,
    title: "Company Swag & Materials",
    description:
      "Branded apparel, jobsite gear, printed materials — designed to be worn and used by real crews, not stuck in a supply closet.",
  },
  {
    icon: Truck,
    title: "Equipment & Fleet Branding",
    description:
      "Complete equipment and vehicle design packages built for high-visibility recognition. Your fleet becomes a billboard on every corridor it runs.",
  },
  {
    icon: Award,
    title: "Jobsite Presence",
    description:
      "Signage, banners, site boards, and field-ready brand touches that make your crews stand out on any project.",
  },
];

/* Why we're different */
export const DIFFERENTIATORS = [
  {
    icon: HardHat,
    title: "Built by a Field Leader",
    body: "Not a software company. Every workflow started as a real problem on a real jobsite.",
  },
  {
    icon: Network,
    title: "Systems + Tech + Consulting",
    body: "Most firms pick one lane. We pair custom systems, machine-control tech, consulting, and brand work so the change actually sticks.",
  },
  {
    icon: Satellite,
    title: "Deep Tech Expertise",
    body: "Machine control, robotic total stations, LPS, and signed-and-sealed models — the stack modern heavy civil actually runs on.",
  },
  {
    icon: Users,
    title: "Practical, Not Bloated",
    body: "Tools your foremen will actually use. No 40-tab software. No consultant-speak. Just what works.",
  },
];

/* Industries served */
export const INDUSTRIES = [
  "Heavy Civil Contractors",
  "Highway & Infrastructure",
  "Earthwork & Site Development",
  "Underground Utilities",
  "Asphalt Paving Operations",
  "Concrete Paving & Slipform",
  "DOT Prime Contractors",
  "Self-Performing GCs",
];

export const STATS = [
  { value: "2015", label: "Founded" },
  { value: "Field-Led", label: "Approach" },
  { value: "Machine", label: "Control Experts" },
  { value: "Nationwide", label: "Coverage" },
];

export const ROLES = [
  "Company Owner / Executive",
  "Operations Manager",
  "Project Manager",
  "Safety Director",
  "Equipment / Shop Manager",
  "Survey / Tech Lead",
  "Other",
];

/* Industry pain points (This Industry Is Broken section) */
export const INDUSTRY_PAIN = [
  "Disconnected systems that don't talk to each other",
  "Paperwork chaos drowning supers and PMs",
  "Software the field never logs into",
  "Crews trained once and never again",
  "Failed tech rollouts that go back in the box",
  "Consultants who never ran a jobsite",
  "Operational bottlenecks nobody owns",
  "Accountability that breaks down at 5:30 AM",
];

/* Authority / expertise areas (Built From Real Operations section) */
export const EXPERTISE_AREAS = [
  "Heavy Civil",
  "FAA",
  "DOT",
  "Utilities",
  "Asphalt",
  "Earthwork",
  "Concrete",
  "Machine Control",
  "Operations Leadership",
  "Infrastructure Technology",
  "Field Execution",
  "Safety Programs",
];

/* Comparison rows (Why ForgedOps section) */
export const FORGEDOPS_VS = [
  {
    forge: "Custom platforms designed around your operation",
    others: "Generic SaaS that forces your crews to fight the tool",
  },
  {
    forge: "Field leaders engaged through execution",
    others: "Consultants who hand off a binder and disappear",
  },
  {
    forge: "Machine control deployed and trained until it sticks",
    others: "Hardware sold and abandoned at the dealer counter",
  },
  {
    forge: "Built by operations professionals who ran the work",
    others: "Software companies who studied construction from a desk",
  },
  {
    forge: "Foreman-first interfaces tested in mud, sun, and gloves",
    others: "Office-built dashboards no one opens after week two",
  },
  {
    forge: "Accountability baked into every workflow",
    others: "Reports that get signed off but never read",
  },
];

/* Live "Operations Intelligence" console mocks for /systems page */
export const OPS_CONSOLE = [
  {
    id: "fleet-status",
    label: "Fleet Status",
    code: "FLT-04",
    metric: "37 / 42",
    metricLabel: "Equipment Active",
    delta: "+3 today",
    deltaPositive: true,
    rows: [
      { name: "CAT 336F", asset: "EXC-014", status: "ACTIVE", color: "ok" },
      { name: "JD 870G", asset: "GRD-002", status: "ACTIVE", color: "ok" },
      { name: "CAT D6T", asset: "DZR-007", status: "MAINT", color: "warn" },
      { name: "Volvo A45G", asset: "ART-011", status: "DOWN", color: "bad" },
    ],
    pulse: true,
  },
  {
    id: "qr-inspection",
    label: "QR Inspection Check-Ins",
    code: "INS-01",
    metric: "184",
    metricLabel: "Today's Scans",
    delta: "98% on-time",
    deltaPositive: true,
    rows: [
      { name: "Pre-shift — Excavator 014", asset: "06:12 AM", status: "PASS", color: "ok" },
      { name: "Pre-shift — Grader 002", asset: "06:18 AM", status: "PASS", color: "ok" },
      { name: "Pre-shift — Dozer 007", asset: "06:24 AM", status: "FLAG", color: "warn" },
      { name: "Pre-shift — Articulated 011", asset: "06:30 AM", status: "PASS", color: "ok" },
    ],
    pulse: true,
  },
  {
    id: "production",
    label: "Production Tracker",
    code: "PRD-02",
    metric: "1,847 CY",
    metricLabel: "Earthwork — Today",
    delta: "+12% vs plan",
    deltaPositive: true,
    rows: [
      { name: "Cell A — Mass Excavation", asset: "742 CY", status: "ON-PACE", color: "ok" },
      { name: "Cell B — Subgrade Prep", asset: "618 CY", status: "ON-PACE", color: "ok" },
      { name: "Cell C — Fill Placement", asset: "487 CY", status: "BEHIND", color: "warn" },
    ],
    pulse: false,
  },
  {
    id: "training-compliance",
    label: "Training Compliance",
    code: "TRN-03",
    metric: "94%",
    metricLabel: "Crew Compliance",
    delta: "6 pending",
    deltaPositive: false,
    rows: [
      { name: "OSHA 10 — Crew A (12)", asset: "Current", status: "OK", color: "ok" },
      { name: "OSHA 30 — Foremen (4)", asset: "Current", status: "OK", color: "ok" },
      { name: "Trench Comp. Person", asset: "Expires 14d", status: "RENEW", color: "warn" },
      { name: "First Aid / CPR (2)", asset: "Expired", status: "ACTION", color: "bad" },
    ],
    pulse: false,
  },
  {
    id: "safety",
    label: "Safety Workflow",
    code: "SAF-05",
    metric: "0",
    metricLabel: "Recordable — 47 Days",
    delta: "Stand-down 06:00",
    deltaPositive: true,
    rows: [
      { name: "Daily JSA — Cell A", asset: "Submitted", status: "OK", color: "ok" },
      { name: "Toolbox Talk — Crew B", asset: "Submitted", status: "OK", color: "ok" },
      { name: "Near-Miss Report", asset: "Open", status: "REVIEW", color: "warn" },
    ],
    pulse: true,
  },
  {
    id: "utility-locate",
    label: "Utility Locate Verification",
    code: "UTL-06",
    metric: "ALL CLEAR",
    metricLabel: "Cell A — Pre-Dig",
    delta: "811 ticket open",
    deltaPositive: true,
    rows: [
      { name: "Gas — Marked", asset: "Yellow", status: "VERIFIED", color: "ok" },
      { name: "Electric — Marked", asset: "Red", status: "VERIFIED", color: "ok" },
      { name: "Comm — Pending", asset: "Orange", status: "WAIT", color: "warn" },
      { name: "Water — Marked", asset: "Blue", status: "VERIFIED", color: "ok" },
    ],
    pulse: false,
  },
];

/* Authority tag micro-statements (used as chip-rows under hero / authority bands) */
export const AUTHORITY_TAGS = [
  "Heavy Civil Focused",
  "FAA / Airfield Specialty",
  "DOT Aligned",
  "Built by Field Leadership",
  "Operationally Deployed",
  "Field-Tested Systems",
];

/* Case studies — anonymized engagement social proof for Home */
export const CASE_STUDIES = [
  {
    id: "rollout",
    type: "Operations Rollout",
    sector: "Heavy Civil · Multi-Region",
    title: "Unified Ops Platform Across 4 Yards.",
    challenge:
      "A regional heavy civil contractor running disconnected tools across 4 yards and 60+ pieces of equipment. Daily reports were faxed. Inspections lived on paper.",
    approach:
      "Stood up one ForgedOps platform — equipment tracking, QR pre-shift inspections, daily reporting, training compliance — built around their cost codes and how their foremen actually run mornings.",
    outcomes: [
      { value: "-42%", label: "Paperwork Time" },
      { value: "+18%", label: "Production Visibility" },
      { value: "92%", label: "Crew Compliance" },
    ],
    timeline: "60 days · Live in week 3",
    media: "/media/op_concrete_crew.jpg",
    mediaPos: "55% 50%",
  },
  {
    id: "recovery",
    type: "Implementation Recovery",
    sector: "DOT / Highway · Regional",
    title: "Recovered a Stalled Software Rollout.",
    challenge:
      "A DOT highway contractor 9 months into a six-figure software rollout. Adoption was stuck at 0%. Crews refused to use it. The system was designed for a different industry.",
    approach:
      "Tore the rollout down to its assumptions, rebuilt workflows around the actual production sequence, and re-engaged crews with onsite training they could trust. No new software purchased.",
    outcomes: [
      { value: "0 → 87%", label: "Adoption in 45 Days" },
      { value: "23", label: "Active Workflows" },
      { value: "$0", label: "New Software Cost" },
    ],
    timeline: "45 days · Onsite + virtual",
    media: "/media/op_pipe_lift.jpg",
    mediaPos: "50% 55%",
  },
  {
    id: "training",
    type: "Workforce Training Program",
    sector: "Airfield · FAA Construction",
    title: "Built Crew Safety Culture on Live AOAs.",
    challenge:
      "An airfield construction contractor scaling onto live taxiway projects with limited FAA-aligned safety experience and uneven competent-person coverage across 4 sites.",
    approach:
      "Onsite OSHA 10 & 30 Outreach, dedicated Airfield Construction Safety program, Competent Person — Excavation, and field-led leadership development for foremen and supers.",
    outcomes: [
      { value: "142", label: "Crew Certified" },
      { value: "0", label: "Recordable Incidents" },
      { value: "4", label: "Active Sites Covered" },
    ],
    timeline: "Ongoing · Quarterly cadence",
    media: "/media/op_airfield_drain.jpg",
    mediaPos: "50% 60%",
  },
];

/* CAPABILITIES — used in PROOF section "Built From Real Operations" */
export const CAPABILITIES = [
  { label: "Heavy Civil", icon: "Truck" },
  { label: "DOT", icon: "Construction" },
  { label: "FAA / Airfield", icon: "Plane" },
  { label: "Utilities", icon: "Cable" },
  { label: "Earthwork", icon: "Mountain" },
  { label: "Asphalt", icon: "RouteOff" },
  { label: "Concrete", icon: "Layers" },
  { label: "Excavation", icon: "Pickaxe" },
  { label: "GPS Machine Control", icon: "Satellite" },
  { label: "OSHA Outreach Training", icon: "ShieldCheck" },
  { label: "Field Deployment Systems", icon: "Radio" },
  { label: "Operations Leadership", icon: "HardHat" },
];

/* Marquee strip — slow drift of credentials beneath PROOF grid */
export const PROOF_MARQUEE = [
  "Heavy Civil",
  "DOT",
  "FAA",
  "Airfield",
  "Earthwork",
  "Utilities",
  "Asphalt",
  "Concrete",
  "Excavation",
  "GPS / Machine Control",
  "OSHA 10 & 30",
  "Trench Safety",
  "Fall Protection",
  "Crane & Rigging",
  "Silica / Table 1",
  "FOD / RSA Discipline",
  "Field Leadership",
  "Operations Intelligence",
];

/* Operations Intelligence highlights */
export const OPS_INTEL = [
  { value: "LIVE", label: "Equipment Status" },
  { value: "QR", label: "Inspection Check-Ins" },
  { value: "AUTO", label: "Daily Reports" },
  { value: "REAL-TIME", label: "Production Tracking" },
];

/* Emotional "Systems Fail" — punchy operational pain points */
export const SYSTEMS_FAIL = [
  {
    title: "Disconnected Software Stacks",
    body: "Three tools for one workflow. None of them talk. Supers spend half the day moving data between tabs.",
  },
  {
    title: "Paperwork Chaos",
    body: "Inspections, JSAs, daily reports — all on paper, all late, all illegible by Friday afternoon.",
  },
  {
    title: "Untrained Crews",
    body: "Tech rolled out at a kickoff meeting and never reinforced. The hardware sits in a Pelican case for a year.",
  },
  {
    title: "No Accountability",
    body: "Equipment goes down, deficiencies stack up, and nobody owns the workflow that closes them out.",
  },
  {
    title: "Consultants Who Never Ran Work",
    body: "Binders full of theory written by people who couldn't run a tailgate meeting if their certification depended on it.",
  },
  {
    title: "Failed Implementations",
    body: "Six-figure software rollouts abandoned within 90 days because nobody designed for how the field actually runs.",
  },
];

/* OSHA Outreach Training offerings (for /training page) */
export const OSHA_OFFERINGS = [
  {
    code: "OSHA 10",
    title: "10-Hour Outreach Training",
    audience: "Crew members, operators, laborers",
    body: "Foundational construction safety training delivered onsite, in-yard, or at your training room. Built around heavy civil hazards crews actually face — not generic warehouse safety scenarios.",
    bullets: [
      "Heavy civil & highway construction focus",
      "Onsite or yard delivery — no classroom required",
      "OSHA-authorized 10-hour curriculum",
      "Real jobsite case studies, not stock examples",
    ],
  },
  {
    code: "OSHA 30",
    title: "30-Hour Outreach Training",
    audience: "Foremen, supers, safety leads, project managers",
    body: "Deeper safety leadership training for supervision and management — the people who set the culture and make the calls. Built so your foremen leave with practical authority, not a binder.",
    bullets: [
      "Foreman & supervisor-focused curriculum",
      "Hazard recognition + corrective action workflows",
      "Safety leadership & accountability development",
      "OSHA-authorized 30-hour completion card",
    ],
  },
];

/* Specialized training programs (12 disciplines) — heavy civil focused */
export const TRAINING_PROGRAMS = [
  {
    id: "excavation",
    title: "Excavation & Trench Safety",
    kicker: "Specialized Program 01",
    focus: "Competent Person",
    bullets: [
      "Competent Person — Excavation",
      "Trench box & shield deployment",
      "Sloping, benching & shoring",
      "Soil classification (Type A/B/C)",
      "Utility locating & damage prevention",
      "Excavation inspections & daily reports",
    ],
  },
  {
    id: "fall-protection",
    title: "Fall Protection",
    kicker: "Specialized Program 02",
    focus: "PFAS / Rescue",
    bullets: [
      "Self-Retracting Lifelines (SRLs)",
      "Personal Fall Arrest Systems (PFAS)",
      "Leading edge & roof work",
      "Rescue planning & retrieval",
      "Aerial & scissor lift safety",
      "Harness inspection & care",
    ],
  },
  {
    id: "heavy-equipment",
    title: "Heavy Equipment Safety",
    kicker: "Specialized Program 03",
    focus: "Operator + Spotter",
    bullets: [
      "Excavators, dozers, rollers, graders, loaders",
      "Blind spot awareness & swing radius",
      "Spotter training & hand signals",
      "Pre-shift inspections & lockout",
      "Pedestrian/equipment separation",
      "Equipment travel & tip-over prevention",
    ],
  },
  {
    id: "focus-four",
    title: "OSHA Focus Four",
    kicker: "Specialized Program 04",
    focus: "Top 4 Killers",
    bullets: [
      "Falls — heavy civil & elevation",
      "Struck-by — equipment, materials, vehicles",
      "Electrocution — overhead & buried lines",
      "Caught-in / between — trenches & equipment",
      "Hazard recognition + control hierarchy",
      "Real-world incident case studies",
    ],
  },
  {
    id: "silica",
    title: "Silica Safety & Table 1",
    kicker: "Specialized Program 05",
    focus: "Respirable Dust",
    bullets: [
      "Silica exposure assessment",
      "Engineering controls & wet methods",
      "Respiratory protection programs",
      "Table 1 compliance for tasks",
      "Medical surveillance requirements",
      "Recordkeeping & exposure controls",
    ],
  },
  {
    id: "confined-space",
    title: "Confined Space Awareness",
    kicker: "Specialized Program 06",
    focus: "Permit-Required",
    bullets: [
      "Permit-required vs. non-permit spaces",
      "Atmospheric hazard identification",
      "Continuous monitoring procedures",
      "Entry permit development",
      "Rescue awareness & retrieval",
      "Communication & attendant duties",
    ],
  },
  {
    id: "loto",
    title: "Lockout / Tagout (LOTO)",
    kicker: "Specialized Program 07",
    focus: "Energy Control",
    bullets: [
      "Hazardous energy identification",
      "Plants, generators, pumps, asphalt & concrete equipment",
      "Authorized vs. affected employees",
      "Group lockout procedures",
      "Maintenance & shutdown protocols",
      "LOTO program development & audits",
    ],
  },
  {
    id: "ppe-ansi",
    title: "PPE & ANSI Training",
    kicker: "Specialized Program 08",
    focus: "ANSI Z89 / Z87 / 107",
    bullets: [
      "High-visibility apparel (ANSI 107)",
      "Hard hat, eye, hearing protection standards",
      "Daily PPE inspection & care",
      "Task-based PPE selection",
      "Replacement & retirement criteria",
      "Crew accountability & enforcement",
    ],
  },
  {
    id: "hazcom",
    title: "Hazard Communication / GHS",
    kicker: "Specialized Program 09",
    focus: "SDS / Labels",
    bullets: [
      "Safety Data Sheet (SDS) interpretation",
      "GHS pictograms & labeling",
      "Chemical handling & storage",
      "Written HazCom program development",
      "Employee right-to-know training",
      "Chemical inventory management",
    ],
  },
  {
    id: "crane-rigging",
    title: "Crane & Rigging Awareness",
    kicker: "Specialized Program 10",
    focus: "Hand Signals / Lift Plans",
    bullets: [
      "Standard crane hand signals",
      "Lift planning & critical-lift criteria",
      "Rigging awareness — slings, shackles, hooks",
      "Capacity calculations & load charts",
      "Spotter & signalperson responsibilities",
      "Pre-lift checklists & exclusion zones",
    ],
  },
  {
    id: "incident-investigation",
    title: "Incident Investigation",
    kicker: "Specialized Program 11",
    focus: "Root Cause / RCA",
    bullets: [
      "Root cause analysis (5-Why, Fishbone)",
      "Near-miss investigation & reporting",
      "Corrective action development",
      "Supervisor response protocols",
      "Documentation & evidence preservation",
      "Lessons-learned dissemination",
    ],
  },
  {
    id: "airfield",
    title: "Airfield Construction Safety",
    kicker: "Specialized Program 12 — Niche Authority",
    focus: "FAA / Live Operations",
    bullets: [
      "FAA airfield orientation & badging",
      "Escort procedures on AOA",
      "Live taxiway & runway operations",
      "Runway safety areas (RSA) discipline",
      "FOD (Foreign Object Debris) prevention",
      "Aviation radio procedures & call-outs",
      "Airfield paving operations safety",
    ],
    isFeatured: true,
  },
];

/* Legacy alias kept for backwards compat — will be removed after pages migrate */
export const TRAINING_TOPICS = [
  "Fall protection (heavy civil-specific)",
  "Trenching & excavation safety",
  "Confined space entry programs",
  "Heavy equipment operator safety",
  "Traffic control & MOT for highway work",
  "Silica & respirable dust compliance",
  "Hot work, electrical & lockout-tagout",
  "Crane & rigging safety",
  "PPE selection & jobsite culture",
  "Stand-down development & delivery",
];

export const TRAINING_AUTHORITY = [
  {
    label: "Field-Led Instruction",
    body: "Every session is delivered by an instructor who has actually run heavy civil work — not a contracted classroom-only safety trainer.",
  },
  {
    label: "Onsite or In-Yard",
    body: "We come to you. Crews train on the jobsite, in the equipment yard, or in your office — wherever execution actually happens.",
  },
  {
    label: "Heavy Civil Focus",
    body: "Earthwork, paving, utilities, DOT, FAA — every hazard scenario tied to the operations your crews actually run, not generic OSHA stock content.",
  },
  {
    label: "Crew-Respected",
    body: "Foremen and operators sit through our training without checking their phones. Because the instructor speaks their language.",
  },
];

/* Social media — set href to "#" to disable a channel until a real URL is live. */
export const SOCIAL = [
  { name: "LinkedIn", icon: "linkedin", href: "https://www.linkedin.com/company/forgedopshq/" },
  { name: "Instagram", icon: "instagram", href: "#" },
  { name: "YouTube", icon: "youtube", href: "#" },
];

/* "Built For" — pre-qualifying sector chips for Home (under cinematic statement) */
export const BUILT_FOR_SECTORS = [
  { label: "Heavy Civil GCs", icon: "Truck" },
  { label: "DOT Primes", icon: "Construction" },
  { label: "FAA Airfield", icon: "PlaneTakeoff" },
  { label: "Underground Utilities", icon: "Cable" },
  { label: "Slipform Paving", icon: "RouteOff" },
  { label: "Self-Performing GCs", icon: "HardHat" },
];

/* CHAOS → CONTROL pairs (Iter 7.2) used inside WHY-SYSTEMS-FAIL section.
 * Operational pressure on the left, ForgedOps outcome on the right.
 */
export const CHAOS_TO_CONTROL = [
  { from: "Paperwork chaos in trucks",  to: "Field visibility on every shift" },
  { from: "Crews bypassing systems",    to: "Workflows crews actually use" },
  { from: "Office / field disconnect",  to: "Real-time operational alignment" },
  { from: "Buried inspections & forms", to: "Auto-routed sign-off + audit trail" },
  { from: "Failed rollout adoption",    to: "Bilingual, brand-fit deployments" },
  { from: "Accountability gaps",        to: "Production traced to crew + shift" },
];

/* HARD-TRUTH pain points — short hard-hitting consequences (Iter 7.2).
 * Replaces the older fragmented INDUSTRY_PAIN + SYSTEMS_FAIL grid.
 */
export const FIELD_HARD_TRUTHS = [
  { title: "The Field Doesn't Wait for Broken Systems.",         body: "Crews keep working. The system gets bypassed within 48 hours." },
  { title: "Crews Won't Use Systems They Don't Trust.",          body: "If it's clunky in the cab, it dies on the truck dashboard." },
  { title: "Paperwork Buried in Trucks Helps Nobody.",           body: "Three weeks later you can't find the sign-off when it matters." },
  { title: "Office and Field Stop Talking the Same Language.",   body: "PMs wait for radio updates. Crews wait for plan sets. Nothing aligns." },
  { title: "Rollouts Die When Supers Feel Sold To.",             body: "Adoption is a culture problem. No software fixes that on its own." },
  { title: "Most Systems Were Designed Without a Foreman in the Room.", body: "Conference-room logic doesn't survive 5:30 AM precon." },
];

/* CAPABILITY_GALLERY — Iter 6.7. Cleaned-up showcase of what the ForgedOps
 * system DOES, DOCUMENTS, IDENTIFIES, and TRACKS — each tile a real, unique
 * field photo paired with a capability whose wording is safe to apply to
 * what's actually in the picture (no equipment-specific claims that
 * contradict the visual content).
 *
 * 36 visually distinct entries, 2 pages of 18. Auto-rotating every ~10s.
 */
export const CAPABILITY_GALLERY = [
  // ============ PAGE 1 — Incidents · Field Docs · Workflows ============
  { src: "/media/jobs/airfield_08.jpg", cat: "DOCUMENTS",  capability: "Damaged Material Receiving Workflow",     saves: "Recovers cost on damaged deliveries before sign-off" },
  { src: "/media/jobs/airfield_02.jpg", cat: "RUNS",       capability: "Pre-Shift Site Walk Checklist",           saves: "Catches hazards before shift start" },
  { src: "/media/jobs/highway_00.jpg",  cat: "RUNS",       capability: "Two-Man Field Audit Sign-Off",            saves: "Cross-checked records, no he-said" },
  { src: "/media/jobs/dallas_04.jpg",   cat: "RUNS",       capability: "Geomembrane / Liner Install QA",          saves: "Locked-in record per panel" },
  { src: "/media/jobs/magdal_04.jpg",   cat: "RUNS",       capability: "Geotextile Install QA + Sign-Off",        saves: "Defensible record of every install" },
  { src: "/media/jobs/airfield_01.jpg", cat: "TRACKS",     capability: "Bulk Material Inventory + Sign-Off",      saves: "Eliminates lost-pipe write-offs" },
  { src: "/media/jobs/highway_05.jpg",  cat: "DOCUMENTS",  capability: "Equipment Incident + Recovery Report",    saves: "Cuts incident close-out time 60%" },
  { src: "/media/jobs/magphoto_00.jpg", cat: "DOCUMENTS",  capability: "Found-Condition Field Report",            saves: "Captures change-order evidence in real time" },
  { src: "/media/jobs/highway_11.jpg",  cat: "DOCUMENTS",  capability: "Vehicle Accident + DOT Logging",          saves: "Auto-routes to insurance + DOT in minutes" },
  { src: "/media/jobs/dallas_05.jpg",   cat: "RUNS",       capability: "Field Crew Coordination Workflow",        saves: "Aligns crews in 5 minutes" },
  { src: "/media/jobs/magdal_01.jpg",   cat: "TRACKS",     capability: "Tank Farm Site Progress Tracking",        saves: "Owner reporting on autopilot" },
  { src: "/media/jobs/highway_02.jpg",  cat: "TRACKS",     capability: "Heavy Equipment Hours Tracking",          saves: "Catches under-utilized assets" },
  { src: "/media/jobs/signbar_07.jpg",  cat: "IDENTIFIES", capability: "Overhead Power Line Hazard Flag",         saves: "Pre-empts utility-strike incidents" },
  { src: "/media/jobs/dallas_00.jpg",   cat: "TRACKS",     capability: "Crew Production Tracking",                saves: "Surfaces production drop within 24 hrs" },
  { src: "/media/jobs/magsep_07.jpg",   cat: "DOCUMENTS",  capability: "Field Reporting + Sign-Off",              saves: "Eliminates lost paperwork" },
  { src: "/media/jobs/signbar_05.jpg",  cat: "RUNS",       capability: "Excavation Hazard Signage Audit",         saves: "OSHA-defensible signage records" },
  { src: "/media/jobs/magdal_08.jpg",   cat: "RUNS",       capability: "Solo Field Inspection Workflow",          saves: "1 super covers more ground" },
  { src: "/media/jobs/dallas_09.jpg",   cat: "TRACKS",     capability: "Material Spread + Compaction Tracking",   saves: "Density data without callbacks" },

  // ============ PAGE 2 — Identifies · Audits · Production ============
  { src: "/media/jobs/highway_01.jpg",  cat: "RUNS",       capability: "Dust Control + Water Truck Schedule",     saves: "Prevents OSHA dust complaints" },
  { src: "/media/jobs/dallas_10.jpg",   cat: "RUNS",       capability: "Bedding Material Placement Workflow",     saves: "Subgrade right the first time" },
  { src: "/media/jobs/magphoto_01.jpg", cat: "DOCUMENTS",  capability: "Soil Density Test Capture",               saves: "QA records owner-deliverable in days" },
  { src: "/media/jobs/signbar_00.jpg",  cat: "RUNS",       capability: "Concrete Barrier Install Audit",          saves: "Locks in MUTCD compliance" },
  { src: "/media/jobs/magsep_05.jpg",   cat: "TRACKS",     capability: "Excavation Production Tracking",          saves: "Surfaces idle / under-used units" },
  { src: "/media/jobs/dallas_02.jpg",   cat: "TRACKS",     capability: "Earthwork + Cut/Fill Tracking",           saves: "Quantity-paid-vs-installed reconciled" },
  { src: "/media/jobs/signbar_08.jpg",  cat: "IDENTIFIES", capability: "Open Excavation Pit Hazard Audit",        saves: "OSHA evidence in incident defense" },
  { src: "/media/jobs/airfield_10.jpg", cat: "TRACKS",     capability: "Pipe Install Production Tracking",        saves: "Real-time install vs plan delta" },
  { src: "/media/jobs/magphoto_02.jpg", cat: "IDENTIFIES", capability: "Open-Excavation Risk Flag",               saves: "Triggers shoring before crew entry" },
  { src: "/media/jobs/magdal_05.jpg",   cat: "RUNS",       capability: "Geogrid Install Verification",            saves: "Locked-in record per panel" },
  { src: "/media/jobs/magphoto_04.jpg", cat: "RUNS",       capability: "Concrete Structure QA + Photo Records",   saves: "Audit-ready documentation" },
  { src: "/media/jobs/magdal_07.jpg",   cat: "TRACKS",     capability: "Truck + Trailer Inventory Check",         saves: "Lost equipment events: zero" },
  { src: "/media/jobs/magsep_10.jpg",   cat: "RUNS",       capability: "Active Excavation Safety Check",          saves: "Pre-entry verification on file" },
  { src: "/media/jobs/magsep_11.jpg",   cat: "TRACKS",     capability: "Bulk Earthwork Movement Tracking",        saves: "Cut/fill balance reconciled" },
  { src: "/media/jobs/airfield_07.jpg", cat: "RUNS",       capability: "Two-Person Layout Verification",          saves: "Cuts rework from layout errors" },
  { src: "/media/jobs/magdal_06.jpg",   cat: "TRACKS",     capability: "Active Equipment Roster + Operators",     saves: "Right operator on right machine" },
  { src: "/media/jobs/dallas_03.jpg",   cat: "DOCUMENTS",  capability: "Site Conditions Photo Log",               saves: "Defensible weather + site record" },
  { src: "/media/jobs/signbar_01.jpg",  cat: "RUNS",       capability: "Site Routing + Barrier Install Audit",    saves: "Cuts haul-route miscommunication" },
];

/* legacy alias */
export const SERVICES = PILLARS;

/* ============ ITER 6.0 — FIELD LEADERSHIP IDENTITY ============ */

/* Manifesto identity strip — "we run the work every day" emotional core */
export const FIELD_LEADERSHIP_MANIFESTO = [
  "Built by people responsible for real production.",
  "Designed by supers who own the outcome.",
  "Tested by crews who have to live with it.",
  "Field-forged operational infrastructure. Not software marketing.",
];

/* Operational consequences — visceral cost-of-failure metrics */
export const OPERATIONAL_COSTS = [
  {
    metric: "30+ hrs",
    label: "Lost Per Crew Per Week",
    body: "Paperwork chaos. Redundant reporting. Systems supers shouldn't have to babysit.",
  },
  {
    metric: "60%",
    label: "Tech Rollouts Fail Field Adoption",
    body: "Designed in conference rooms. Abandoned in trenches. Crews bypass what they don't trust.",
  },
  {
    metric: "0",
    label: "Inspections Logged = Blind Risk",
    body: "Buried inspection sheets. Missing JSAs. The line between a near-miss and a recordable event.",
  },
  {
    metric: "$1M+",
    label: "Production Lost to Bottlenecks",
    body: "Untracked downtime. Idle iron. Accountability gaps. Margin bleeding out month after month.",
  },
];

/* Field Adoption pillars — bilingual + white-label combined identity */
export const FIELD_ADOPTION = [
  {
    kicker: "Built For Real Field Adoption",
    title: "Bilingual Systems Crews Can Use.",
    body: "Fully bilingual English / Spanish workflows — not Google-translated, but field-tested for adoption. If your crew can't read the system, your crew won't use the system.",
    bullets: [
      "Bilingual forms & inspections",
      "Bilingual safety & training content",
      "Bilingual onboarding & instructions",
      "Per-user language toggle",
    ],
  },
  {
    kicker: "Customer-Branded Platforms",
    title: "Systems That Feel Internal.",
    body: "Crews adopt systems faster when the platform looks and feels like their own company. Choose ForgedOps branding or an optional white-label deployment with your brand, colors, and terminology.",
    bullets: [
      "Customer-branded portals",
      "Custom forms, workflows & approval paths",
      "Custom training content & libraries",
      "Branded PDFs, reports & notifications",
    ],
  },
];



